import { UserInfoBannerRow } from "./UserBannerInfoRow";
import { EmailIcon } from "assets/icons/EmailIcon";
import { MacosIcon } from "assets/icons/ComputerIcon";
import { LinuxIcon } from "assets/icons/LinuxIcon";
import { WindowsIcon } from "assets/icons/WindowIcon";
import { ProviderIcon } from "assets/icons/ProviderIcon";
import { DnsIcon } from "assets/icons/DnsIcon";
import { CityIcon } from "assets/icons/CityIcon";
import { RegionIcon } from "assets/icons/RegionIcon";
import { CoordinatesIcon } from "assets/icons/CoordinatesIcon";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useEffect, useState } from "react";
import { getBrowserIcon } from "react-browser-icons";
import { useIntl } from "react-intl";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";
import { getCountryCodeForFlag } from "utils/countryMapping";
import { useIntlContext } from "context/TranslationContext";
import { Box, Heading, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, SimpleGrid, Button, Text, useToast } from "@chakra-ui/react";
import MapComponent from "./MapComponent";

type UserInfoBannerRowType = {
  label: string;
  icon?: JSX.Element | string;
  text: string | JSX.Element;
};

type InfoSection = {
  title: string;
  rows: UserInfoBannerRowType[];
};

interface IpInfoResponse {
  city: string;
  country: string;
  hostname: string;
  ip: string;
  loc: string;
  org: string;
  postal: string;
  region: string;
  timezone: string;
}

enum Browser {
  Chrome = "Chrome",
  Safari = "Safari",
  Firefox = "Firefox",
  Opera = "Opera",
  IE = "IE",
  Edge = "Edge",
  "Mobile Safari" = "Mobile Safari",
  Brave = "Brave",
  Samsung = "Samsung",
  Chromium = "Chromium",
}

const getBrowserInfo = () => {
  const userAgent = window.navigator.userAgent;
  let browserName = "";
  let browserShort = "";
  let browserVersion = "";

  if (userAgent.indexOf("Firefox") > -1) {
    browserName = "Mozilla Firefox";
    browserVersion = userAgent.substring(userAgent.indexOf("Firefox") + 8);
    browserShort = "Firefox";
  } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
    browserName = "Opera";
    browserVersion = userAgent.substring(userAgent.indexOf("Version") + 8);
    browserShort = "Opera";
  } else if (userAgent.indexOf("Trident") > -1) {
    browserName = "Microsoft Internet Explorer";
    browserVersion = userAgent.substring(userAgent.indexOf("rv") + 3);
    browserShort = "IE";
  } else if (userAgent.indexOf("Edge") > -1) {
    browserName = "Microsoft Edge";
    browserVersion = userAgent.substring(userAgent.indexOf("Edge") + 5);
    browserShort = "Edge";
  } else if (userAgent.indexOf("Chrome") > -1) {
    browserName = "Google Chrome";
    browserVersion = userAgent.substring(userAgent.indexOf("Chrome") + 7);
    browserShort = "Chrome";
  } else if (userAgent.indexOf("Safari") > -1) {
    browserName = "Safari";
    browserVersion = userAgent.substring(userAgent.indexOf("Safari") + 7);
    browserShort = "Safari";
  }

  let OSName = "Unknown OS";
  if (navigator.userAgent.includes("Win")) OSName = "Windows";
  if (navigator.userAgent.includes("Mac")) OSName = "Mac OS";
  if (navigator.userAgent.includes("Linux")) OSName = "Linux";
  if (navigator.userAgent.includes("Android")) OSName = "Android";
  if (navigator.userAgent.includes("iOS")) OSName = "iOS";

  let OSVersion: any = "Unknown Version";
  if (/Windows/.test(navigator.userAgent)) {
    const match = /Windows (.*)/.exec(navigator.userAgent);
    OSVersion = match ? match[1] : "Unknown Version";
  } else if (/Mac/.test(navigator.userAgent)) {
    // Attempt to extract Mac OS X / macOS version
    const macMatch = /Mac OS X (10[._\d]+)/.exec(navigator.userAgent) || 
                      /Mac OS X (\d+[._]\d+)/.exec(navigator.userAgent) ||
                      /Intel Mac OS X (10[._\d]+)/.exec(navigator.userAgent) ||
                      /Intel Mac OS X (\d+[._]\d+)/.exec(navigator.userAgent);
    if (macMatch) {
      OSVersion = macMatch[1].replace(/_/g, '.');
    } else {
      // For new macOS versions (11+)
      const newMacMatch = /Mac OS X (\d+[._]\d+)/.exec(navigator.userAgent);
      if (newMacMatch) {
        OSVersion = newMacMatch[1].replace(/_/g, '.');
      } else {
        OSVersion = "macOS";
      }
    }
  } else if (/Android/.test(navigator.userAgent)) {
    const match = /Android ([._\d]+)/.exec(navigator.userAgent);
    OSVersion = match ? match[1] : "Unknown Version";
  } else if (/iOS/.test(navigator.userAgent)) {
    const match = /OS (\d+)_(\d+)_?(\d+)?/.exec(navigator.userAgent);
    if (match) {
      OSVersion = `${match[1]}.${match[2]}.${match[3] || 0}`;
    }
  } else if (/Linux/.test(navigator.userAgent)) {
    OSVersion = "Linux";
  }
  return {
    browser: {
      name: browserName,
      version: browserVersion,
      short: browserShort,
    },
    os: {
      name: OSName,
      version: OSVersion,
    },
  };
};

export const UserBannerInfo = () => {
  const intl = useIntl();
  const { locale } = useIntlContext();

  const userIpDefaultData = {
    city: "",
    country: "",
    hostname: "",
    ip: "",
    loc: "",
    org: "",
    postal: "",
    region: "",
    timezone: "",
  };

  const [userIpData, setUserIpData] = useState<IpInfoResponse | null>(
    userIpDefaultData
  );
  const [browserIcon, setBrowserIcon] = useState<any>(null);
  const [osIcon, setOsIcon] = useState<any>(null);
  const [currentTime, setCurrentTime] = useState<string>("");
  const [currentDate, setCurrentDate] = useState<string>("");
  const [localTime, setLocalTime] = useState<string>("");
  const [systemTime, setSystemTime] = useState<string>("");
  const [utcTime, setUtcTime] = useState<string>("");
  const [gmtTime, setGmtTime] = useState<string>("");
  const [screenResolution, setScreenResolution] = useState<string>("");
  const [viewportSize, setViewportSize] = useState<string>("");
  const [screenData, setScreenData] = useState<{
    colorDepth: number;
    pixelDepth: number;
    height: number;
    width: number;
    availHeight: number;
    availWidth: number;
    availTop: number;
    availLeft: number;
  } | null>(null);
  const [sunrise, setSunrise] = useState<string>("");
  const [sunset, setSunset] = useState<string>("");
  const [browserLocation, setBrowserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [streetAddress, setStreetAddress] = useState<string | null>(null);

  const userIp = useSelector((state: RootState) => state.userIp.userIp);
  const userIpDetails = useSelector((state: RootState) => state.userIpDetails);

  const { browser, os } = getBrowserInfo();

  // Function to calculate sunrise/sunset
  const calculateSunriseSunset = (lat: number, lon: number, date: Date) => {
    // Improved formula for calculating sunrise/sunset
    const dayOfYear = Math.floor((date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 86400000);
    
    // Solar declination
    const declination = 23.45 * Math.sin((360 * (284 + dayOfYear) / 365) * Math.PI / 180);
    
    // Equation of time (approximate)
    const B = (360 / 365) * (dayOfYear - 81);
    const equationOfTime = 9.87 * Math.sin(2 * B * Math.PI / 180) - 7.53 * Math.cos(B * Math.PI / 180) - 1.5 * Math.sin(B * Math.PI / 180);
    
    // Timezone (hours from UTC)
    const timezoneOffset = date.getTimezoneOffset() / 60;
    const timeCorrection = equationOfTime + 4 * lon - 60 * timezoneOffset;
    
    // Solar noon (in hours)
    const solarNoon = 12 + timeCorrection / 60;
    
    // Sunrise/sunset angle
    const latRad = lat * Math.PI / 180;
    const declRad = declination * Math.PI / 180;
    const hourAngle = Math.acos(-Math.tan(latRad) * Math.tan(declRad));
    
    // Sunrise and sunset time
    const sunriseTime = solarNoon - (hourAngle * 12 / Math.PI);
    const sunsetTime = solarNoon + (hourAngle * 12 / Math.PI);
    
    const formatTime = (decimalHours: number) => {
      let hours = Math.floor(decimalHours);
      let minutes = Math.floor((decimalHours - hours) * 60);
      
      // Handle negative values and values > 24
      if (hours < 0) hours += 24;
      if (hours >= 24) hours -= 24;
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    };
    
    return {
      sunrise: formatTime(sunriseTime),
      sunset: formatTime(sunsetTime),
    };
  };

  // Update time every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString(locale);
      const dateStr = now.toLocaleDateString(locale);
      setCurrentTime(timeStr);
      setCurrentDate(dateStr);
      
      // Short format for Local time and System time
      const shortFormat = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(now);
      setLocalTime(shortFormat);
      setSystemTime(shortFormat);
      
      // UTC and GMT time with localization
      const utcFormat = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC',
      }).format(now);
      setUtcTime(`${utcFormat} GMT`);
      setGmtTime(`${utcFormat} GMT`);
    };
    
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [locale]);

  // Get screen resolution and viewport size
  useEffect(() => {
    const updateScreenInfo = () => {
      setScreenResolution(`${window.screen.width}x${window.screen.height}`);
      setViewportSize(`${window.innerWidth}x${window.innerHeight}`);
      setScreenData({
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth,
        height: window.screen.height,
        width: window.screen.width,
        availHeight: window.screen.availHeight,
        availWidth: window.screen.availWidth,
        availTop: (window.screen as any).availTop || 0,
        availLeft: (window.screen as any).availLeft || 0,
      });
    };
    
    updateScreenInfo();
    window.addEventListener('resize', updateScreenInfo);
    return () => window.removeEventListener('resize', updateScreenInfo);
  }, []);

  useEffect(() => {
    const browserIcon = getBrowserIcon({
      browser: Browser[browser.short],
      className: "class",
      style: { borderRadius: "100%", width: "20px", height: "20px", opacity: 0.7 },
      size: 20,
    });

    setBrowserIcon(browserIcon);
    //eslint-disable-next-line
  }, []);

  // Calculate sunrise/sunset when coordinates change
  useEffect(() => {
    if (userIpData?.loc) {
      const coords = userIpData.loc.split(',');
      if (coords.length === 2) {
        const lat = parseFloat(coords[0]);
        const lon = parseFloat(coords[1]);
        if (!isNaN(lat) && !isNaN(lon)) {
          const sunTimes = calculateSunriseSunset(lat, lon, new Date());
          setSunrise(sunTimes.sunrise);
          setSunset(sunTimes.sunset);
        }
      }
    }
    //eslint-disable-next-line
  }, [userIpData?.loc]);

  useEffect(() => {
    if (userIpDetails) {
      // Use full data from own API /check-ip
      const fullIpData = {
        ip: userIpDetails.ip,
        city: userIpDetails.city,
        country: userIpDetails.country,
        hostname: userIpDetails.hostname,
        loc: userIpDetails.loc,
        org: userIpDetails.organization,
        postal: userIpDetails.postal,
        region: userIpDetails.region,
        timezone: userIpDetails.time_zone?.name || "",
      };
      
      setUserIpData(fullIpData);
    } else if (userIp) {
      // Fallback: use only IP address if full data not yet loaded
      const basicIpData = {
        ip: userIp,
        city: "Loading...",
        country: "Loading...", 
        hostname: "",
        loc: "",
        org: "",
        postal: "",
        region: "",
        timezone: "",
      };
      
      setUserIpData(basicIpData);
    }
    //eslint-disable-next-line
  }, [userIp, userIpDetails]);

  useEffect(() => {
    switch (os.name) {
      case "Windows":
        setOsIcon(<WindowsIcon />);
        break;
      case "Mac OS":
        setOsIcon(<MacosIcon />);
        break;
      case "Linux":
        setOsIcon(<LinuxIcon />);
        break;
    }
    //eslint-disable-next-line
  }, []);

  // Get coordinates from loc (format: "lat,lon") or from browser geolocation
  const coordinates = browserLocation 
    ? [browserLocation.lat.toString(), browserLocation.lon.toString()]
    : userIpData?.loc ? userIpData.loc.split(',') : null;
  const coordinatesText = coordinates ? `${coordinates[0]}, ${coordinates[1]}` : "no data";

  const toast = useToast();

  // Function to get address by coordinates (reverse geocoding)
  const fetchStreetAddress = async (lat: number, lon: number) => {
    try {
      // Use Nominatim API from OpenStreetMap (free, no API key required)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'IPScore Website' // Required by Nominatim
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch address');
      }
      
      const data = await response.json();
      
      if (data && data.address) {
        // Build address from available fields
        const address = data.address;
        const parts = [];
        
        if (address.road) parts.push(address.road);
        if (address.house_number) parts.push(address.house_number);
        if (address.suburb && !address.road) parts.push(address.suburb);
        if (address.city && !address.road) parts.push(address.city);
        
        const streetName = parts.length > 0 
          ? parts.join(', ')
          : address.display_name?.split(',')[0] || null;
        
        return streetName;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching street address:', error);
      return null;
    }
  };

  // Load address when coordinates change
  useEffect(() => {
    if (coordinates && coordinates.length === 2) {
      const lat = parseFloat(coordinates[0]);
      const lon = parseFloat(coordinates[1]);
      
      if (!isNaN(lat) && !isNaN(lon)) {
        fetchStreetAddress(lat, lon).then((address) => {
          setStreetAddress(address);
        });
      }
    } else {
      setStreetAddress(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserLocation, userIpData?.loc]);

  // Function to request browser geolocation
  const requestBrowserLocation = async () => {
    if (!navigator.geolocation) {
      toast({
        title: intl.formatMessage({ id: "GeolocationNotSupported", defaultMessage: "Geolocation is not supported by your browser" }),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsRequestingLocation(true);
    setLocationError(null);

    // Check permission status via Permissions API (if available)
    let permissionStatus = null;
    if (navigator.permissions && navigator.permissions.query) {
      try {
        permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      } catch (e) {
        // Permissions API may not be supported or have different implementation
      }
    }

    // If permission was denied, show instructions
    if (permissionStatus && permissionStatus.state === 'denied') {
      setIsRequestingLocation(false);
      const errorMessage = intl.formatMessage({ 
        id: "LocationPermissionDeniedInstructions", 
        defaultMessage: "Please enable location permission in your browser settings and try again" 
      });
      setLocationError(errorMessage);
      toast({
        title: intl.formatMessage({ id: "LocationPermissionDenied", defaultMessage: "Location permission denied" }),
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setBrowserLocation({ lat: latitude, lon: longitude });
        setIsRequestingLocation(false);
        setLocationError(null);
        toast({
          title: intl.formatMessage({ id: "LocationUpdated", defaultMessage: "Location updated" }),
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      },
      (error) => {
        setIsRequestingLocation(false);
        let errorMessage = intl.formatMessage({ id: "LocationError", defaultMessage: "Failed to get location" });
        let errorDescription = "";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = intl.formatMessage({ id: "LocationPermissionDenied", defaultMessage: "Location permission denied" });
            errorDescription = intl.formatMessage({ 
              id: "LocationPermissionDeniedInstructions", 
              defaultMessage: "Please enable location permission in your browser settings. Click the lock icon in the address bar or go to browser settings." 
            });
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = intl.formatMessage({ id: "LocationUnavailable", defaultMessage: "Location information unavailable" });
            break;
          case error.TIMEOUT:
            errorMessage = intl.formatMessage({ id: "LocationTimeout", defaultMessage: "Location request timeout" });
            break;
        }
        
        setLocationError(errorDescription || errorMessage);
        toast({
          title: errorMessage,
          description: errorDescription || undefined,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Section: Browser and OS (combined)
  const browserOsSection: InfoSection = {
    title: intl.formatMessage({ id: "BrowserAndOS" }),
    rows: [
    {
      label: intl.formatMessage({ id: "YourBrowser" }),
      icon: browserIcon,
      text: `${browser.name} ${browser.version}`,
    },
    {
        label: intl.formatMessage({ id: "OperatingSystem" }),
        icon: osIcon,
        text: `${os.name} ${os.version}`,
      },
      {
        label: "Platform",
        icon: osIcon,
        text: navigator.platform || "Unknown",
      },
    ],
  };

  // Section: Screen
  const screenSection: InfoSection = {
    title: intl.formatMessage({ id: "ScreenInfo" }),
    rows: [
      {
        label: intl.formatMessage({ id: "ScreenResolution" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenResolution || "Loading...",
      },
      {
        label: intl.formatMessage({ id: "ViewportSize" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: viewportSize || "Loading...",
      },
      {
        label: intl.formatMessage({ id: "ColorDepth" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? screenData.colorDepth.toString() : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "PixelDepth" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? screenData.pixelDepth.toString() : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "ScreenHeight" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? screenData.height.toString() : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "ScreenWidth" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? screenData.width.toString() : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "AvailHeight" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? screenData.availHeight.toString() : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "AvailWidth" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? screenData.availWidth.toString() : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "AvailTop" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? (screenData.availTop.toString() || "N/A") : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "AvailLeft" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: screenData ? (screenData.availLeft.toString() || "N/A") : "Loading...",
      },
      {
        label: intl.formatMessage({ id: "WindowSize" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: viewportSize ? `${viewportSize} (${screenResolution})` : "Loading...",
      },
    ],
  };


  // Section: Date and time
  const dateTimeSection: InfoSection = {
    title: intl.formatMessage({ id: "DateTimeInfo" }),
    rows: [
      {
        label: intl.formatMessage({ id: "CurrentDate" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: currentDate || "Loading...",
      },
      {
        label: intl.formatMessage({ id: "LocalTime" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: localTime || currentTime || "Loading...",
      },
      {
        label: intl.formatMessage({ id: "SystemTime" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: systemTime || "Loading...",
      },
      {
        label: intl.formatMessage({ id: "UTCTime" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: utcTime || "Loading...",
      },
      {
        label: intl.formatMessage({ id: "GMTTime" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: gmtTime || "Loading...",
      },
      {
        label: intl.formatMessage({ id: "TimeZone" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: userIpDetails?.time_zone?.name || userIpData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
      },
      {
        label: intl.formatMessage({ id: "DaylightSaving" }),
        icon: <div style={{ width: "20px", height: "20px" }} />,
        text: userIpDetails?.time_zone?.is_dst ? intl.formatMessage({ id: "YesText" }) : intl.formatMessage({ id: "NoText" }),
      },
      ...(sunrise && sunset ? [
        {
          label: intl.formatMessage({ id: "Sunrise" }),
          icon: <div style={{ width: "20px", height: "20px" }} />,
          text: sunrise,
        },
        {
          label: intl.formatMessage({ id: "Sunset" }),
          icon: <div style={{ width: "20px", height: "20px" }} />,
          text: sunset,
        },
      ] : []),
    ],
  };

  // Section: ISP information
  const ispSection: InfoSection = {
    title: intl.formatMessage({ id: "ISPInfo" }),
    rows: [
      {
        label: intl.formatMessage({ id: "YourProvider" }),
        icon: <ProviderIcon />,
        text: userIpData?.org || "no data",
      },
      {
        label: intl.formatMessage({ id: "City" }),
        icon: <CityIcon />,
        text: userIpData?.city || "no data",
      },
      {
        label: intl.formatMessage({ id: "Region" }),
        icon: <RegionIcon />,
        text: userIpData?.region || "no data",
      },
      {
        label: intl.formatMessage({ id: "Country" }),
      icon: userIpData?.country && userIpData.country !== "Loading..." ? (
        <img
          src={`/flags/${getCountryCodeForFlag(userIpData.country)}.svg`}
          width="20px"
          height="20px"
          alt={userIpData.country}
          style={{ opacity: 0.7 }}
        />
      ) : (
        <div style={{ width: "20px", height: "20px" }} />
      ),
        text: userIpData?.country || "no data",
    },
    {
      label: intl.formatMessage({ id: "ZIP" }),
      icon: <EmailIcon />,
      text: userIpData?.postal || "no data",
    },
      {
        label: intl.formatMessage({ id: "Coordinates" }),
        icon: <CoordinatesIcon />,
        text: coordinatesText,
      },
    ],
  };

  // Add DNS if available
  if (userIpData?.hostname) {
    ispSection.rows.push({
      label: intl.formatMessage({ id: "DNS" }),
      icon: <DnsIcon />,
      text: <span>{userIpData.hostname}</span>,
    });
  }

  // Block order: ISP, Detailed Info (spoiler with Browser+OS, Screen and Date inside)
  const regularSections: InfoSection[] = [ispSection];
  const detailedInfoSections: InfoSection[] = [browserOsSection, screenSection, dateTimeSection];

  // Cards are surfaces (rule 7: brand/status text sits on a surface, not on
  // the page ground); elevation is the theme's `md` shadow (none in dark,
  // where the surface ladder and the border carry it).
  const cardBg = "bg.surface";
  const borderColor = "border.subtle";
  const headingColor = "text.primary";
  const dividerColor = "border.subtle";
  const errorBgColor = "status.errorSubtle";
  const errorBorderColor = "status.error";
  const errorTextColor = "status.error";
  // Helper text: text.secondary at full opacity. red.500 at opacity 0.8 read
  // 3.77:1; any token at 0.8 drops under 4.5:1 on errorSubtle.
  const errorHelpTextColor = "text.secondary";
  const boxShadow = "md";

  return (
    <>
      {userIpData.city ? (
        <Box width="100%" mt={6} maxWidth="1400px" mx="auto">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            {/* Regular blocks: ISP */}
            {regularSections.map((section, sectionIndex) => (
              <Box
                key={sectionIndex}
                p={5}
                borderRadius="12px"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow={boxShadow}
                bg={cardBg}
                width="100%"
              >
                <Heading size="sm" mb={4} color={headingColor} fontWeight={600}>
                  {section.title}
                </Heading>
                {section.rows.map((row, rowIndex) => (
                  <UserInfoBannerRow props={row} key={rowIndex} />
                ))}
              </Box>
            ))}

            {/* Map as separate card after ISP Info */}
            {coordinates && coordinates.length === 2 && (
              <Box
                p={5}
                borderRadius="12px"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow={boxShadow}
                bg={cardBg}
                width="100%"
              >
                <Heading size="sm" mb={4} color={headingColor} fontWeight={600}>
                  {intl.formatMessage({ id: "LocationMap", defaultMessage: "Location Map" })}
                </Heading>
                <MapComponent
                  lat={parseFloat(coordinates[0])}
                  lon={parseFloat(coordinates[1])}
                  streetAddress={streetAddress}
                />
                <Button
                  mt={4}
                  size="sm"
                  variant="outline"
                  onClick={requestBrowserLocation}
                  isLoading={isRequestingLocation}
                  width="100%"
                  colorScheme="blue"
                >
                  {intl.formatMessage({ id: "CheckLocation", defaultMessage: "Check My Location" })}
                </Button>
                {locationError && (
                  <Box mt={3} p={3} borderRadius="8px" bg={errorBgColor} borderWidth="1px" borderColor={errorBorderColor}>
                    <Text fontSize="xs" color={errorTextColor} textAlign="center" mb={2}>
                      {locationError}
                    </Text>
                    <Text fontSize="xs" color={errorHelpTextColor} textAlign="center">
                      {intl.formatMessage({ 
                        id: "LocationPermissionHelp", 
                        defaultMessage: "Enable location in browser settings (lock icon in address bar)" 
                      })}
                    </Text>
                    <Button
                      mt={2}
                      size="xs"
                      variant="ghost"
                      onClick={requestBrowserLocation}
                      width="100%"
                      colorScheme="red"
                    >
                      {intl.formatMessage({ id: "TryAgain", defaultMessage: "Try Again" })}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Blocks under common spoiler: Detailed Info (Screen and Date inside) */}
            <Accordion allowToggle width="100%" gridColumn={{ base: "1", md: "1 / -1" }}>
              <AccordionItem
                borderRadius="12px"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow={boxShadow}
                bg={cardBg}
              >
                <AccordionButton>
                  <Box flex="1" textAlign="center">
                    <Heading size="sm" color={headingColor} fontWeight={600}>
                      {intl.formatMessage({ id: "DetailedInfo" })}
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Box
                    display={{ base: "flex", md: "grid" }}
                    flexDirection={{ base: "column", md: "row" }}
                    gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={5}
                    alignItems="start"
                  >
                    {/* Left column: blocks 1 and 3 */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={5}
                      width="100%"
                    >
                      {detailedInfoSections
                        .filter((_, index) => index === 0 || index === 2)
                        .map((section, filteredIndex) => {
                          const originalIndex = filteredIndex === 0 ? 0 : 2;
                          const isLastInColumn = filteredIndex === 1;
                          return (
                            <Box key={originalIndex} width="100%">
                              {isLastInColumn && (
                                <Box
                                  borderTop="1px solid"
                                  borderColor={dividerColor}
                                  mb={5}
                                  mt={3}
                                  width="100%"
                                />
                              )}
                              <Heading size="sm" mb={4} color={headingColor} fontWeight={600}>
                                {section.title}
                              </Heading>
                              {section.rows.map((row, rowIndex) => (
                                <UserInfoBannerRow props={row} key={rowIndex} />
                              ))}
                            </Box>
                          );
                        })}
                    </Box>

                    {/* Right column: block 2 */}
                    <Box width="100%">
                      {detailedInfoSections
                        .filter((_, index) => index === 1)
                        .map((section) => (
                          <Box key={1} width="100%">
                            <Heading size="sm" mb={4} color={headingColor} fontWeight={600}>
                              {section.title}
                            </Heading>
                            {section.rows.map((row, rowIndex) => (
                              <UserInfoBannerRow props={row} key={rowIndex} />
                            ))}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </SimpleGrid>
        </Box>
      ) : (
        <LoaderSpinner />
      )}
    </>
  );
};
